import { useState } from 'react';
import { api, vendorCopy } from './_shop-helpers';

interface SessionResponse {
  session?: unknown;
}

interface StartSessionProps {
  vendor: string;
  token: string;
  isVerified: boolean;
  availableCents: number | null;
  balanceLoading: boolean;
  balanceError: string | null;
  onStarted?: (session: unknown) => void;
}

function formatCurrencyFromCents(cents: number | null): string {
  if (cents === null || !Number.isFinite(cents)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export default function StartSession({
  vendor,
  token,
  isVerified,
  availableCents,
  balanceLoading,
  balanceError,
  onStarted,
}: StartSessionProps) {
  const copy = (vendorCopy as Record<string, { label: string }>)[vendor] ?? { label: vendor };
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const formattedAmount = amount.replace(/[^\d.]/g, '').slice(0, 10);
  const hasBalanceInfo = availableCents !== null && Number.isFinite(availableCents);
  const hasFunds = hasBalanceInfo && (availableCents as number) > 0;
  const availableLabel = balanceLoading
    ? 'Checking…'
    : formatCurrencyFromCents(hasBalanceInfo ? availableCents : null);

  async function handleStart() {
    try {
      setBusy(true);
      setError('');
      setMessage('');

      if (!formattedAmount) {
        setError('Enter an amount');
        return;
      }

      const dollars = Number.parseFloat(formattedAmount);
      if (!Number.isFinite(dollars) || dollars <= 0) {
        setError('Enter a valid amount');
        return;
      }

      if (!hasBalanceInfo) {
        setError('Your available balance is still loading. Try again in a moment.');
        return;
      }

      const centsRequested = Math.round(dollars * 100);
      if (availableCents === null || availableCents <= 0) {
        setError('You do not have any available balance right now.');
        return;
      }

      if (centsRequested > availableCents) {
        setError('That amount exceeds your available balance.');
        return;
      }

      const response = await api('teacher-session-attach', { vendor, amountUsd: dollars }, token) as SessionResponse;
      setAmount('');
      setMessage(`Session started for ${copy.label}`);
      onStarted?.(response.session || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start session');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 rounded-2xl border border-sand bg-white p-6 shadow-sm">
      <div className="space-y-1">
        <h4 className="text-base font-semibold text-cocoa">Start a new {copy.label} session</h4>
        <p className="text-sm text-cocoa/70">
          Enter your intended spend. We’ll attach your verified card and track this session.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block flex-1 text-sm font-medium text-cocoa">
          Amount (USD)
          <input
            value={formattedAmount}
            onChange={(event) => {
              setAmount(event.target.value);
              setError('');
              setMessage('');
            }}
            placeholder="e.g. 250.00"
            inputMode="decimal"
            className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
          />
        </label>
        <button
          type="button"
          onClick={handleStart}
          disabled={
            busy ||
            !isVerified ||
            !formattedAmount ||
            balanceLoading ||
            !hasBalanceInfo ||
            !hasFunds
          }
          className="btn whitespace-nowrap disabled:cursor-not-allowed disabled:bg-sand disabled:text-cocoa/60"
        >
          {busy ? 'Starting…' : 'Start session'}
        </button>
      </div>

      <p className="text-xs text-cocoa/70">Available balance: {availableLabel}</p>

      {!isVerified ? (
        <p className="text-sm text-cocoa/70">Verify your card above to enable session tracking.</p>
      ) : null}
      {!balanceLoading && hasBalanceInfo && availableCents !== null && availableCents <= 0 ? (
        <p className="text-sm text-brick">You're out of funds right now.</p>
      ) : null}
      {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
      {balanceError ? <p className="text-sm text-brick">{balanceError}</p> : null}
      {error ? <p className="text-sm text-brick">{error}</p> : null}
    </div>
  );
}
